import { onlineContractStep } from "constants/onlineContract";
import {
  ApplicationStatusNumberValue,
  ApplicationStatusValue,
  INVALID_APPLICATION_STATUS_FOR_RENTAL_INFORMATION,
  AdditionalInfo,
} from "constants/status";
import { ContractTypeEnum } from "graphql/type.ts";
import { isDefined } from "utils/isDefined.util";

export type PathType =
  | "confirm"
  | "review"
  | "contract"
  | "under-contract"
  | "cancelled-contract"
  | "document-issuance"
  | "rental-information"
  | "chat";

export const APPLICATION_STEP_STATUS = {
  UNCOMPLETED: "未完了",
  COMPLETED: "完了済",
  REJECTED: "手続き終了",
} as const;

const APPLICATION_STATUS = {
  applicationReceived: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "申込内容の確認を完了してください",
  },
  // Application is rejected
  applicationRejected: {
    status: APPLICATION_STEP_STATUS.REJECTED,
    message: "手続きを終了しました",
  },
  applicationAcceptance: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "申込内容の確認を完了しました",
  },
  underReview: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "審査を完了してください",
  },
  reviewRejected: {
    status: APPLICATION_STEP_STATUS.REJECTED,
    message: "手続きを終了しました",
  },
  reviewCompleted: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "審査を完了しました",
  },
  contract: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約書、区画図、請求書をアップロードして、契約手続きを完了してください",
  },
  contractRejected: {
    status: APPLICATION_STEP_STATUS.REJECTED,
    message: "手続きを終了しました",
  },
  contractCompleted: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "契約を完了しました",
  },
  contractTerminated: {
    status: APPLICATION_STEP_STATUS.REJECTED,
    message: "手続きを終了しました",
  },
  // Contract form not submitted yet
  contractNotSubmitted: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約フォームを送信してください",
  },
  // Contract form submitted but lessee hasn't responded
  contractSubmitted: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "契約フォームを送信しました",
  },
  // Lessee submitted form but lessor hasn't confirmed
  contractReview: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約内容を確認してください",
  },
  confirmContractContent: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約内容を確認してください",
  },
  createContract: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約書を作成してください",
  },
  requestSign: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約書を確認の上、契約書の確認と押印を依頼してください",
  },
  confirmAndRequestStamp: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "押印済みとなりましたら、契約手続きを完了させてください",
  },
  approveContract: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "契約手続きを完了しました",
  },
  unUploadPdfContract: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "締結完了済み契約書をアップロードしてください",
  },
  uploadedPdfContract: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "契約書をアップロードしました",
  },
  createdCreditManual: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "契約情報を確認して、契約手続きを完了させてください",
  },
  contractCancelAcceptance: {
    status: APPLICATION_STEP_STATUS.UNCOMPLETED,
    message: "解約日を確定してください",
  },
  contractCancelSchedule: {
    status: APPLICATION_STEP_STATUS.COMPLETED,
    message: "解約日を確定しました",
  },
} as const;

const getMessageForStatusInContract = ({
  contractConfirmationStep,
  applicationContract,
  additionalInfo,
}: {
  contractConfirmationStep: number | undefined;
  applicationContract?: ContractTypeEnum;
  additionalInfo?: AdditionalInfo;
}) => {
  if (applicationContract === ContractTypeEnum.DocumentContract) {
    const stepTimestamps = additionalInfo?.step_timestamps;
    if (!stepTimestamps?.contract_creation_at) {
      return APPLICATION_STATUS.createContract;
    }

    if (!stepTimestamps?.sign_and_upload_at) {
      return APPLICATION_STATUS.unUploadPdfContract;
    }

    if (!stepTimestamps?.initial_payment_at) {
      return APPLICATION_STATUS.uploadedPdfContract;
    }

    if (stepTimestamps?.initial_payment_at) {
      return APPLICATION_STATUS.createdCreditManual;
    }

    return APPLICATION_STATUS.approveContract;
  } else {
    switch (contractConfirmationStep) {
      // Case 1:
      // Lessee has submitted the form on My page and sent it back to Lessor, but Lessor has not pressed [Complete confirm]
      // 2.1: 未完了 (Uncompleted)
      // 2.2: 契約内容を確認してください (Please check contract content)
      // 2.3: Do not display
      case onlineContractStep.CONFIRM_CONTRACT_CONTENT:
        return APPLICATION_STATUS.confirmContractContent;

      // Case 2:
      // Lessor pressed [Complete confirm] but has not pressed [Create contract]
      // 2.1: 未完了 (Uncompleted)
      // 2.2: 契約書を作成してください (Please create contract)
      // 2.3: Do not display
      case onlineContractStep.CREATE_CONTRACT:
        return APPLICATION_STATUS.createContract;

      // Case 3:
      // Lessor pressed [Create contract] but has not pressed [Confirm contract & Request sign]
      // 2.1: 未完了 (Uncompleted)
      // 2.2: 契約書を確認の上、契約書の確認と押印を依頼してください (Please confirm contract and request signing)
      // 2.3: Do not display
      case onlineContractStep.REQUEST_SIGN:
        return APPLICATION_STATUS.requestSign;

      // Case 4:
      // Lessor pressed [Confirm contract & Request sign] but has not pressed [Approve contract]
      // 2.1: 未完了 (Uncompleted)
      // 2.2: 押印済みとなりましたら、契約手続きを完了させてください (After signing is completed, please finish contract procedure)
      // 2.3: Do not display
      case onlineContractStep.CONFIRM_AND_REQUEST_STAMP:
        return APPLICATION_STATUS.confirmAndRequestStamp;

      // Case 5:
      // Pressed [Approve contract]
      // 2.1: 完了済 (Completed)
      // 2.2: 契約手続きを完了しました (Contract procedure completed)
      // 2.3: 完了 yyyy/mm/dd (Completed at approve contract date)
      // yyyy/mm/dd = application.contract_done_date
      default:
        return APPLICATION_STATUS.approveContract;
    }
  }
};

export const getMessageForStatus = ({
  status,
  pathname,
  contractConfirmationStep,
  applicationContract,
  additionalInfo,
}: {
  status: ApplicationStatusValue;
  pathname: PathType;
  contractConfirmationStep: number | undefined;
  applicationContract?: ContractTypeEnum;
  additionalInfo?: AdditionalInfo;
}) => {
  switch (status) {
    case "申込受付":
      return APPLICATION_STATUS.applicationReceived;

    // ステータスが”〇〇NG” or ”契約破棄”の場合
    //   すべての画面で手続き終了
    case "申込NG":
    case "審査NG":
    case "契約NG":
      return APPLICATION_STATUS.applicationRejected;

    // ステータスが"審査"の時
    //   申込確認画面: 申込確認完了
    //   審査画面: 審査未完了
    case "審査":
      return pathname == "confirm"
        ? APPLICATION_STATUS.applicationAcceptance
        : pathname === "review"
        ? APPLICATION_STATUS.underReview
        : APPLICATION_STATUS.contract;

    // ステータスが"契約"の時
    //   申込確認画面: 申込確認完了
    //   審査画面: 審査完了
    //   契約画面: 契約未完了
    case "契約":
      if (pathname === "confirm") {
        return APPLICATION_STATUS.applicationAcceptance;
      }
      if (pathname === "review") {
        return APPLICATION_STATUS.reviewCompleted;
      }

      // For contract pathname, use detailed logic
      if (pathname === "contract") {
        return getMessageForStatusInContract({
          contractConfirmationStep,
          applicationContract,
          additionalInfo,
        });
      }

      // Default fallback
      return APPLICATION_STATUS.contractNotSubmitted;

    // ステータスが”契約完了”の時
    //   申込確認画面: 申込確認完了
    //   審査画面: 審査完了
    //   契約画面: 契約完了
    case "契約中":
      // For contract pathname, use detailed logic
      if (pathname === "contract") {
        return APPLICATION_STATUS.approveContract;
      }

      if (pathname === "under-contract") {
        return APPLICATION_STATUS.contractCancelAcceptance;
      }

      return pathname === "confirm"
        ? APPLICATION_STATUS.applicationAcceptance
        : pathname === "review"
        ? APPLICATION_STATUS.reviewCompleted
        : APPLICATION_STATUS.contractCompleted;
    // status = 8 CANCELLATION_ACCEPTANCE
    case "解約受付":
      if (pathname === "confirm") {
        return APPLICATION_STATUS.applicationAcceptance;
      }

      if (pathname === "review") {
        return APPLICATION_STATUS.reviewCompleted;
      }

      // For contract pathname, use detailed logic
      if (pathname === "contract") {
        return APPLICATION_STATUS.approveContract;
      }

      return APPLICATION_STATUS.contractCancelAcceptance;
    // status = 9 CANCELLATION_SCHEDULED
    case "解約予定":
    case "強制解約":
    case "通常解約":
      if (pathname === "confirm") {
        return APPLICATION_STATUS.applicationAcceptance;
      }

      if (pathname === "review") {
        return APPLICATION_STATUS.reviewCompleted;
      }

      // For contract pathname, use detailed logic
      if (pathname === "contract") {
        return APPLICATION_STATUS.approveContract;
      }

      return APPLICATION_STATUS.contractCancelSchedule;

    default:
      return APPLICATION_STATUS.applicationReceived;
  }
};

export const allowedApplicationPath = ({
  pathname,
  status,
}: {
  status: ApplicationStatusNumberValue | undefined;
  pathname: PathType | "";
}): { canAccess: boolean; from: PathType | null } => {
  if (
    isDefined(status) &&
    INVALID_APPLICATION_STATUS_FOR_RENTAL_INFORMATION.includes(status) &&
    pathname === "rental-information"
  ) {
    return { canAccess: false, from: "" as PathType };
  }

  switch (status) {
    case 0:
    case 1:
    case 2:
      switch (pathname) {
        case "confirm":
          return { canAccess: true, from: null };
        case "review":
        case "contract":
        case "under-contract":
        case "cancelled-contract":
          return { canAccess: false, from: "confirm" };
        default:
          return { canAccess: true, from: null };
      }
    case 3:
    case 4:
      switch (pathname) {
        case "confirm":
        case "review":
          return { canAccess: true, from: null };
        case "contract":
        case "under-contract":
        case "cancelled-contract":
          return { canAccess: false, from: "review" };
        default:
          return { canAccess: true, from: null };
      }
    case 5:
    case 6:
      switch (pathname) {
        case "confirm":
        case "review":
        case "contract":
          return { canAccess: true, from: null };
        case "under-contract":
        case "cancelled-contract":
          return { canAccess: false, from: "contract" };
        default:
          return { canAccess: true, from: null };
      }
    case 7:
    case 8:
    case 9:
      switch (pathname) {
        case "confirm":
        case "review":
        case "contract":
        case "under-contract":
          return { canAccess: true, from: null };
        case "cancelled-contract":
          return { canAccess: false, from: "under-contract" };
        default:
          return { canAccess: true, from: null };
      }
    case 11:
      switch (pathname) {
        case "confirm":
        case "review":
        case "contract":
        case "under-contract":
        case "cancelled-contract":
        case "document-issuance":
        case "rental-information":
        case "chat":
          return { canAccess: true, from: null };
        default:
          return { canAccess: false, from: "cancelled-contract" };
      }
    default:
      return { canAccess: true, from: null };
  }
};
